import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Calendar } from "lucide-react";
import { fetchJson, assetUrl } from "../lib/api";
import { formatDateFR } from "../lib/format";
import { CARD_SECTION_LABELS, METHOD_LABELS } from "../lib/constants";
import FighterImage from "../components/FighterImage";
import Countdown from "../components/Countdown";
import ShareButton from "../components/ShareButton";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const groupBySection = (fights) => {
  const groups = { main_event: [], co_main: [], main_card: [], prelims: [] };
  (fights || []).forEach((f) => {
    if (!groups[f.card_section]) groups[f.card_section] = [];
    groups[f.card_section].push(f);
  });
  Object.keys(groups).forEach((k) =>
    groups[k].sort((a, b) => a.card_position - b.card_position),
  );
  return groups;
};

const FightRow = ({ fight, completed }) => {
  const a = fight.fighter_a || {};
  const b = fight.fighter_b || {};
  const winnerId = fight.result?.winner_id;
  const aWin = completed && winnerId === a.id;
  const bWin = completed && winnerId === b.id;

  return (
    <Link
      to={`/fights/${fight.slug}`}
      data-testid={`fightcard-row-${fight.slug}`}
      className="block border-b border-neutral-900 hover:bg-neutral-950/60 transition-colors duration-200 group"
    >
      <div className="grid grid-cols-12 items-stretch">
        {/* Fighter A */}
        <div className="col-span-5 md:col-span-5 flex items-center gap-3 md:gap-4 p-3 md:p-5">
          <div className="w-14 h-14 md:w-20 md:h-20 flex-shrink-0">
            <FighterImage
              src={assetUrl(a.photo_url)}
              alt={a.first_name}
              className="w-full h-full"
              imgClassName="object-cover object-top"
            />
          </div>
          <div className="min-w-0 flex-1 md:text-right">
            <p className="font-display font-black text-white text-xl md:text-3xl leading-none truncate">
              {a.last_name || a.first_name}
            </p>
            <p className="mt-1 font-stats text-[10px] md:text-xs text-neutral-500 uppercase tracking-widest">
              {a.display_record?.record_str || a.record?.record_str}
            </p>
            {aWin && (
              <span className="mt-1 inline-block px-2 py-0.5 text-[10px] font-stats uppercase tracking-widest border border-burkina-green/60 text-burkina-green">
                WIN
              </span>
            )}
          </div>
        </div>

        {/* VS + info */}
        <div className="col-span-2 md:col-span-2 flex flex-col items-center justify-center p-2 md:p-4 border-x border-neutral-900">
          <span className="font-display font-black text-2xl md:text-4xl text-gold">
            VS
          </span>
          {fight.weight_class?.name && (
            <p className="mt-1 font-stats text-[9px] md:text-xs text-neutral-500 uppercase tracking-widest text-center">
              {fight.weight_class?.name?.split(" (")[0]}
            </p>
          )}
          {fight.number_rounds ? (
            <p className="font-stats text-[9px] md:text-xs text-neutral-600 uppercase tracking-widest">
              {fight.number_rounds} rd
            </p>
          ) : null}
          {fight.is_title_fight && (
            <span
              className="mt-1 inline-flex items-center px-1.5 py-0.5 font-stats text-[8px] md:text-[10px] uppercase tracking-widest border border-gold text-gold"
              data-testid={`fight-title-fight-badge-${fight.slug}`}
              title="Combat pour le titre"
            >
              ★ Titre
            </span>
          )}
        </div>

        {/* Fighter B */}
        <div className="col-span-5 md:col-span-5 flex items-center gap-3 md:gap-4 p-3 md:p-5">
          <div className="min-w-0 flex-1">
            <p className="font-display font-black text-white text-xl md:text-3xl leading-none truncate">
              {b.last_name || b.first_name}
            </p>
            <p className="mt-1 font-stats text-[10px] md:text-xs text-neutral-500 uppercase tracking-widest">
              {b.display_record?.record_str || b.record?.record_str}
            </p>
            {bWin && (
              <span className="mt-1 inline-block px-2 py-0.5 text-[10px] font-stats uppercase tracking-widest border border-burkina-green/60 text-burkina-green">
                WIN
              </span>
            )}
          </div>
          <div className="w-14 h-14 md:w-20 md:h-20 flex-shrink-0">
            <FighterImage
              src={assetUrl(b.photo_url)}
              alt={b.first_name}
              className="w-full h-full"
              imgClassName="object-cover object-top"
            />
          </div>
        </div>
      </div>

      {completed && fight.result && (
        <div className="px-4 md:px-6 py-2 md:py-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-neutral-950 bg-black/40 font-stats text-[10px] md:text-xs uppercase tracking-widest text-neutral-400">
          <span className="text-gold">
            {METHOD_LABELS[fight.result.method] || fight.result.method}
          </span>
          <span>R{fight.result.round}</span>
          <span>{fight.result.time}</span>
        </div>
      )}
    </Link>
  );
};

const SectionBlock = ({ title, fights, completed }) => {
  if (!fights || fights.length === 0) return null;
  return (
    <div className="mt-10">
      <div className="flex items-baseline gap-4">
        <h3 className="font-display font-black text-2xl md:text-4xl text-white uppercase">
          {title}
        </h3>
        <span className="h-px flex-1 bg-neutral-900" />
        <span className="font-stats text-xs text-neutral-500 uppercase tracking-widest">
          {fights.length} combat{fights.length > 1 ? "s" : ""}
        </span>
      </div>
      <div className="mt-4 border-t border-b border-neutral-900">
        {fights.map((f) => (
          <FightRow key={f.id} fight={f} completed={completed} />
        ))}
      </div>
    </div>
  );
};

export default function EventDetail() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJson(`/events/${slug}`);
        setEv(data);
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [slug]);

  useDocumentMeta({
    title: ev
      ? `${ev.name}${ev.date ? ` · ${(ev.date || "").slice(0, 10)}` : ""}`
      : "",
    description: ev
      ? `${ev.venue || ""}${ev.city ? " · " + ev.city : ""} · BUFL Fight Night`
      : "",
    image: ev?.poster_url ? assetUrl(ev.poster_url) : null,
  });

  if (notFound) {
    return (
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 min-h-[60vh]">
        <h1 className="font-display font-black text-5xl md:text-8xl text-white">
          ÉVÉNEMENT INTROUVABLE
        </h1>
        <Link
          to="/events"
          className="inline-flex items-center gap-3 mt-10 px-6 py-3 border border-white text-white font-display uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors duration-200"
        >
          Voir tous les événements →
        </Link>
      </section>
    );
  }

  if (!ev) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-neutral-600 font-stats text-xs uppercase tracking-widest animate-pulse">
          Chargement…
        </p>
      </div>
    );
  }

  const isCompleted = ev.status === "completed";
  const sections = groupBySection(ev.fights);

  return (
    <div data-testid="event-detail-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden border-b border-neutral-900">
        {ev.poster_url && (
          <div className="absolute inset-0 -z-10">
            <img
              src={assetUrl(ev.poster_url)}
              alt=""
              aria-hidden
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          </div>
        )}
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-14 md:pt-24 pb-14 md:pb-24 min-h-[60vh] flex flex-col justify-end relative">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
            {isCompleted ? "Résultats officiels" : "Prochaine fight night"}{" "}
            {ev.edition_number ? `· Édition ${String(ev.edition_number).padStart(2, "0")}` : ""}
          </p>
          <h1
            className="mt-4 font-display font-black text-white leading-[0.85] break-words max-w-full"
            style={{ fontSize: "clamp(2.25rem, 10vw, 9rem)" }}
            data-testid="event-name"
          >
            {ev.name}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-stats text-sm md:text-base text-neutral-300 uppercase tracking-widest">
            <span className="inline-flex items-center gap-2">
              <Calendar size={14} className="text-gold" />
              {formatDateFR(ev.date, { withGmtTime: true })}
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin size={14} className="text-gold" />
              {ev.venue} · {ev.city}
            </span>
            <span className="ml-auto">
              <ShareButton
                title={ev.name}
                text={`${ev.name} — ${formatDateFR(ev.date)} · ${ev.venue || ""} ${ev.city || ""}`}
                testId="event-share"
              />
            </span>
          </div>
          {!isCompleted && (
            <div className="mt-8">
              <Countdown target={ev.date} testId="event-countdown" />
            </div>
          )}
          {ev.description && (
            <p className="mt-8 max-w-3xl text-neutral-400 text-base md:text-lg leading-relaxed">
              {ev.description}
            </p>
          )}
        </div>
      </section>

      {/* Fight card */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-stats">
          {isCompleted ? "Résultats" : "Fight card"}
        </p>
        <h2 className="mt-4 font-display font-black text-4xl md:text-6xl text-white leading-none">
          {isCompleted ? "OFFICIAL RESULTS" : "FIGHT CARD"}
        </h2>

        {(!ev.fights || ev.fights.length === 0) ? (
          <div className="mt-10 border border-neutral-900 line-grid py-20 px-8 text-center">
            <p className="font-display font-black text-3xl md:text-5xl text-neutral-600">
              FIGHT CARD À VENIR
            </p>
          </div>
        ) : (
          <>
            <SectionBlock
              title={CARD_SECTION_LABELS.main_event}
              fights={sections.main_event}
              completed={isCompleted}
            />
            <SectionBlock
              title={CARD_SECTION_LABELS.co_main}
              fights={sections.co_main}
              completed={isCompleted}
            />
            <SectionBlock
              title={CARD_SECTION_LABELS.main_card}
              fights={sections.main_card}
              completed={isCompleted}
            />
            <SectionBlock
              title={CARD_SECTION_LABELS.prelims}
              fights={sections.prelims}
              completed={isCompleted}
            />
          </>
        )}
      </section>
    </div>
  );
}
